import { isOutsideDateRange } from '../common/dateUtils';
import { PythonBackendToFrontend } from '../common/mappings';
import { hasSaleIssue } from '../common/flagBadSaleHelper';
import logger from './logger';

// Error messages mapping
const ERROR_MESSAGES = {
  'NALSale': 'This is a non-arms-length sale',
  'InventoryMismatch': 'This property was renovated, but the sale reflects the old inventory.',
  'SaleConditionIssue': 'This comp has a sale condition issue (sold as is)',
  'BadIFMV': 'This comp has a bad IFMV value',
  'OutsideDateRange': 'This sale is outside the set date range.',
  'notMLS': 'This comp does not have an MLS number.',
  'returnWindow': 'This is outside the top 100 comps, but is saved.',
  'AcreageFilter': 'This comp has acreage below the minimum percentage threshold.',
  'SquareFeetFilter': 'This comp has square footage below the minimum percentage threshold.',
  'GrossAdjFilter': 'This comp has gross adjustments above the maximum percentage threshold.',
  'NetAdjFilter': 'This comp has net adjustments above the maximum percentage threshold.',
  'MissingCoordinates': 'This comp is missing latitude and/or longitude coordinates.',
};


// Only thing to confirm when revisiting this, is that the errorflags aren't redundant compared to our comprehensive error checking function
// and make sure they aren't logically different
/**
 * Generate error flags for a property based on various criteria
 * @param {Object} processedData - Property data object
 * @param {Object} negotiationObj - Negotiation object with settings
 * @returns {Object} Object containing error flags
 */
export const generateErrorFlags = (processedData, negotiationObj) => {
  const nalError = processedData.ALFlag === 0;
  const inventoryMismatchError = hasSaleIssue(processedData.SaleIssue, 'InventoryMismatch');
  const saleConditionError = hasSaleIssue(processedData.SaleIssue, 'SaleConditionIssue');
  const badIFMVError = processedData.BadIFMV;
  const outsideDateRangeError = isOutsideDateRange(
    processedData.SaleDate, 
    negotiationObj.earliestCutoffDate, 
    negotiationObj.latestCutoffDate
  );
  const notMLSError = (negotiationObj.mlsCompsOnly && (!processedData.MLSNumber || processedData.MLSNumber === null));
  
  // Check for missing coordinates
  const lat = processedData.latitude || processedData.Latitude;
  const lng = processedData.longitude || processedData.Longitude;
  const missingCoordinatesError = !lat || !lng || isNaN(parseFloat(lat)) || isNaN(parseFloat(lng));
  
  const hasOtherErrors = nalError || inventoryMismatchError || saleConditionError || badIFMVError || outsideDateRangeError || notMLSError || missingCoordinatesError;
  
  return {
    ALFlag: processedData.ALFlag ?? 1,
    InventoryMismatch: inventoryMismatchError ? 1 : 0,
    SaleConditionIssue: saleConditionError ? 1 : 0,
    BadIFMV: processedData.BadIFMV ?? false,
    OutsideDateRange: outsideDateRangeError ? 1 : 0,
    notMLS: notMLSError ? 1 : 0,
    MissingCoordinates: missingCoordinatesError ? 1 : 0,
    returnWindow: hasOtherErrors ? 0 : 1
  };
};

/**
 * Generate error codes from error flags
 * @param {Object} errorFlags - Object containing error flags
 * @returns {Array} Array of error code strings
 */
export const generateErrorCodes = (errorFlags) => {
  const errorCodes = [];
  if (errorFlags.ALFlag === 0) errorCodes.push('NALSale');
  if (errorFlags.InventoryMismatch === 1) errorCodes.push('InventoryMismatch');
  if (errorFlags.SaleConditionIssue === 1) errorCodes.push('SaleConditionIssue');
  if (errorFlags.BadIFMV) errorCodes.push('BadIFMV');
  if (errorFlags.OutsideDateRange === 1) errorCodes.push('OutsideDateRange');
  if (errorFlags.notMLS === 1) errorCodes.push('notMLS');
  if (errorFlags.MissingCoordinates === 1) errorCodes.push('MissingCoordinates');
  if (errorFlags.returnWindow === 1) errorCodes.push('returnWindow');
  return errorCodes;
};

/**
 * Generate error messages from error codes
 * @param {Array} errorCodes - Array of error code strings
 * @returns {Array} Array of error objects with code and message
 */
export const generateErrorMessages = (errorCodes) => {
  return errorCodes.map(errorCode => ({
    code: errorCode,
    message: ERROR_MESSAGES[errorCode] || errorCode
  }));
};

/**
 * Helper function to create errors array for basic error checks
 * @param {Object} comp - Property data object
 * @returns {Array} Array of error codes
 */
export const createErrorsArray = (comp) => {
  const errors = [];
  if (comp.ALFlag === 0) {
    errors.push('NALSale');
  }
  if (hasSaleIssue(comp.SaleIssue, 'InventoryMismatch')) {
    errors.push('InventoryMismatch');
  }
  if (hasSaleIssue(comp.SaleIssue, 'SaleConditionIssue')) {
    errors.push('SaleConditionIssue');
  }
  return errors;
};

/**
 * Comprehensive error checking function for comp rows
 * @param {Object} row - Row data object
 * @param {Object} subject - Subject property data
 * @param {Object} userFilterSettings - User filter settings
 * @param {Function} getAdjustments - Function to get adjustments
 * @param {boolean} isAssessmentRun - Whether this is an assessment run (uses IFMV instead of SalePrice)
 * @returns {Object} Object containing errorCodes and errorMessages
 */
export const checkCompErrors = (row, subject = null, userFilterSettings = {}, getAdjustments = null, isAssessmentRun = false) => {
  const errors = [];
  
  // Basic error checks (always applied)
  if (hasSaleIssue(row.SaleIssue, 'SaleConditionIssue')) {
    errors.push('SaleConditionIssue');
  }
  if (row.BadIFMV) {
    errors.push('BadIFMV');
  }
  if (row.notMLS === 1) {
    errors.push('notMLS');
  }
  
  // Check for missing coordinates
  const lat = row.latitude || row.Latitude;
  const lng = row.longitude || row.Longitude;
  
  if (!lat || !lng || isNaN(parseFloat(lat)) || isNaN(parseFloat(lng))) {
    errors.push('MissingCoordinates');
  }
  
  // Sale-specific error checks - Skip for assessment runs since we use IFMV, not sales
  if (!isAssessmentRun) {
    // AL Flag check
    if (row.ALFlag === 0) {
      errors.push('NALSale');
    }
    if (hasSaleIssue(row.SaleIssue, 'InventoryMismatch')) {
      errors.push('InventoryMismatch');
    }
    
    // OutsideDateRange check from pre-set flag
    if (row.OutsideDateRange === 1) {
      errors.push('OutsideDateRange');
    }
    
    // Date Range Filtering (only if subject and filter settings are provided)
    if (subject && userFilterSettings && userFilterSettings.dateRange && userFilterSettings.dateRange.length === 2) {
      const [startDate, endDate] = userFilterSettings.dateRange;
      const saleDate = row.SaleDate;
      
      if (saleDate) {
        const parseLocalDate = (dateStr) => {
          if (!dateStr) return null;
          const date = new Date(dateStr);
          return isNaN(date.getTime()) ? null : date;
        };
        
        const saleDateObj = parseLocalDate(saleDate);
        
        if (saleDateObj) {
          // Apply start date filter if provided
          if (startDate) {
            const startDateObj = parseLocalDate(startDate);
            if (startDateObj && saleDateObj < startDateObj) {
              errors.push('OutsideDateRange');
            }
          }
          
          // Apply end date filter if provided
          if (endDate) {
            const endDateObj = parseLocalDate(endDate);
            if (endDateObj && saleDateObj > endDateObj) {
              errors.push('OutsideDateRange');
            }
          }
        }
      }
    }
  }
  
  // Filter-based error checks (only if subject and filter settings are provided)
  if (subject && userFilterSettings) {
    
    // Acreage Percentage Filtering
    if (userFilterSettings.acreageFilterEnabled && userFilterSettings.acreageFilter && subject.Acres) {
      const subjectAcres = parseFloat(subject.Acres) || 0;
      const compAcres = parseFloat(row.Acres) || 0;
      
      if (subjectAcres > 0) {
        const percentage = (compAcres / subjectAcres) * 100;
        if (percentage < userFilterSettings.acreageFilter) {
          errors.push('AcreageFilter');
        }
      }
    }
    
    // Square Feet Percentage Filtering
    if (userFilterSettings.squareFeetFilterEnabled && userFilterSettings.squareFeetFilter && subject.Sqft) {
      const subjectSqft = parseFloat(subject.Sqft) || 0;
      const compSqft = parseFloat(row.Sqft) || 0;
      
      if (subjectSqft > 0) {
        const percentage = (compSqft / subjectSqft) * 100;
        if (percentage < userFilterSettings.squareFeetFilter) {
          errors.push('SquareFeetFilter');
        }
      }
    }
    
    // Adjustment percentage filtering
    if (getAdjustments && userFilterSettings) {

      // Create a row object for adjustment checking using proper field mapping
      const rowForAdjustmentCheck = {
        SalePrice: row.SalePrice,
        IFMV: row.IFMV,
        ...Object.fromEntries(
          Object.keys(getAdjustments)
            .map(adjKey => {
              const feKey = PythonBackendToFrontend[adjKey];
              if (feKey && row[feKey] !== undefined) {
                return [adjKey, row[feKey]];
              }
              return [adjKey, undefined];
            })
            .filter(([key, value]) => value !== undefined)
        )
      };

      // Gross Adjustment Percentage Filtering
      if (userFilterSettings.grossAdjPctFilterEnabled && userFilterSettings.grossAdjPctFilter) {
        let grossAdjustment = 0;
        
        Object.entries(getAdjustments).forEach(([adjKey, factor]) => {
          if (
            factor == null ||
            adjKey === 'manuallyAddedComps' ||
            adjKey === 'specificPid' ||
            typeof subject[adjKey] === 'undefined' ||
            typeof rowForAdjustmentCheck[adjKey] === 'undefined'
          ) return;
          
          // Only calculate for numeric values
          const subjectVal = parseFloat(subject[adjKey]);
          const compVal = parseFloat(rowForAdjustmentCheck[adjKey]);
          if (!isNaN(subjectVal) && !isNaN(compVal)) {
            const diff = subjectVal - compVal;
            grossAdjustment += Math.abs(diff * factor);
          }
        });
        
        const getCompPrice = (row) => {
          // Use the correct price field based on run type - no fallbacks
          const compPrice = isAssessmentRun 
            ? parseFloat(row.IFMV) || 0
            : parseFloat(row.SalePrice) || 0;
          
          // DEBUG: Log price selection for specific parcel
          if (row.parcel_id === 'W2000-049-017-00001-000-0007') {
            logger.log('🔍 Gross Adj - Price Selection:', {
              parcel_id: row.parcel_id,
              isAssessmentRun,
              salePrice: parseFloat(row.SalePrice) || 0,
              ifmv: parseFloat(row.IFMV) || 0,
              selectedPrice: compPrice,
              priceSource: isAssessmentRun ? 'IFMV' : 'SalePrice'
            });
          }
          
          return compPrice;
        };
        
        const compPrice = getCompPrice(rowForAdjustmentCheck);
        if (compPrice > 0) {
          const grossAdjPercentage = (grossAdjustment / compPrice) * 100;
          
          if (grossAdjPercentage > userFilterSettings.grossAdjPctFilter) {
            errors.push('GrossAdjFilter');
          }
        } 
      }

      // Net Adjustment Percentage Filtering
      if (userFilterSettings.netAdjPctFilterEnabled && userFilterSettings.netAdjPctFilter) {
        let netAdjustment = 0;
        
        Object.entries(getAdjustments).forEach(([adjKey, factor]) => {
          if (
            factor == null ||
            adjKey === 'manuallyAddedComps' ||
            adjKey === 'specificPid' ||
            typeof subject[adjKey] === 'undefined' ||
            typeof rowForAdjustmentCheck[adjKey] === 'undefined'
          ) return;
          
          // Only calculate for numeric values
          const subjectVal = parseFloat(subject[adjKey]);
          const compVal = parseFloat(rowForAdjustmentCheck[adjKey]);
          if (!isNaN(subjectVal) && !isNaN(compVal)) {
            const diff = subjectVal - compVal;
            netAdjustment += diff * factor;
          }
        });
        
        const getCompPrice = (row) => {
          // Use the correct price field based on run type - no fallbacks
          const compPrice = isAssessmentRun 
            ? parseFloat(row.IFMV) || 0
            : parseFloat(row.SalePrice) || 0;
          
          // DEBUG: Log price selection for specific parcel
          if (row.parcel_id === 'W2000-049-017-00001-000-0007') {
            logger.log('🔍 Net Adj - Price Selection:', {
              parcel_id: row.parcel_id,
              isAssessmentRun,
              salePrice: parseFloat(row.SalePrice) || 0,
              ifmv: parseFloat(row.IFMV) || 0,
              selectedPrice: compPrice,
              priceSource: isAssessmentRun ? 'IFMV' : 'SalePrice'
            });
          }
          
          return compPrice;
        };
        
        const compPrice = getCompPrice(rowForAdjustmentCheck);
        if (compPrice > 0) {
          const netAdjPercentage = Math.abs((netAdjustment / compPrice) * 100);
          
          // DEBUG: Log net adjustment calculation for specific parcel
          if (row.parcel_id === 'W2000-049-017-00001-000-0007') {
            logger.log('🔍 Net Adjustment Calculation:', {
              parcel_id: row.parcel_id,
              netAdjustment,
              compPrice,
              netAdjPercentage,
              filterThreshold: userFilterSettings.netAdjPctFilter,
              passed: netAdjPercentage <= userFilterSettings.netAdjPctFilter,
              errorAdded: netAdjPercentage > userFilterSettings.netAdjPctFilter
            });
          }
          
          if (netAdjPercentage > userFilterSettings.netAdjPctFilter) {
            errors.push('NetAdjFilter');
          }
        } else {
          if (row.parcel_id === 'W2000-049-017-00001-000-0007') {
            logger.log('🔍 Net Adj - No comp price available:', {
              salePrice: rowForAdjustmentCheck.SalePrice,
              ifmv: rowForAdjustmentCheck.IFMV,
              compPrice,
              isAssessmentRun,
              expectedField: isAssessmentRun ? 'IFMV' : 'SalePrice'
            });
          }
        }
      }
    }
  }
  
  // Add returnWindow error if no other errors exist
  if (errors.length === 0 && row.returnWindow === 1) {
    errors.push('returnWindow');
  }
  
  // Generate error messages
  const errorMessages = generateErrorMessages(errors);
  
  return {
    errorCodes: errors,
    errorMessages: errorMessages
  };
};
